import type { CSSProperties } from 'react';

export const LOGO_BG_RE = /^#[0-9a-f]{6}$/i;

export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result));
    r.onerror = () => rej(new Error('read'));
    r.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = () => rej(new Error('image'));
    img.src = src;
  });
}

export async function composeLogoOnBackground(file: File, bg: string): Promise<{ dataUrl: string; mime: string }> {
  const src = await readFileAsDataUrl(file);
  const img = await loadImage(src);
  const iw = img.naturalWidth || 600;
  const ih = img.naturalHeight || 600;
  const pad = 0.05;
  const inner = { w: iw, h: ih };
  let w = inner.w / (1 - 2 * pad);
  let h = inner.h / (1 - 2 * pad);
  if (w < h) w = h;
  if (w > h * 2.2) h = w / 2.2;
  const scale = 640 / Math.max(w, h);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(w * scale);
  canvas.height = Math.round(h * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  const dw = Math.round(inner.w * scale);
  const dh = Math.round(inner.h * scale);
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, Math.round((canvas.width - dw) / 2), Math.round((canvas.height - dh) / 2), dw, dh);
  return { dataUrl: canvas.toDataURL('image/png'), mime: 'image/png' };
}

export function logoFrameStyle(logoBg: string | null | undefined): CSSProperties {
  return logoBg && LOGO_BG_RE.test(logoBg)
    ? ({ background: logoBg, padding: 0, '--logo-bg': logoBg } as CSSProperties)
    : { background: '#fff' };
}

export function logoFrameAttrs(logoBg: string | null | undefined): { style: CSSProperties; 'data-bg'?: string } {
  const style = logoFrameStyle(logoBg);
  return logoBg && LOGO_BG_RE.test(logoBg) ? { style, 'data-bg': '' } : { style };
}
